// Web onboarding copy — board «25 ERP Web Onboarding v2» (2026-10-14) and
// HANDOFF-erp-web §14.3, verbatim (ar-IQ MSA). Terminology-bound sentences are
// functions of the business's term profile (design-system/locales.json via
// @/lib/terminology): the boards draw a clinic, so «المراجعين» there is
// «الزبائن» for a salon — never a hard-coded category word (rule 7).

import { placeTerms, term, type TermProfile } from '@/lib/terminology';

type ProfileWords = Partial<Record<TermProfile, string>> & { default: string };

/** Accusative/genitive plural of the customer noun («يستقبل المراجعين», «للمراجعين»; the hotel's «النزلاء», board 27). */
export function customersAcc(profile: TermProfile): string {
  const words: ProfileWords = { default: 'الزبائن', clinic: 'المراجعين', gulf: 'العملاء', hotel: 'النزلاء' };
  return words[profile] ?? words.default;
}
/** «للمراجعين» / «للزبائن» / «للعملاء» — the preposition fused with the definite plural. */
export function forCustomers(profile: TermProfile): string {
  return `لل${customersAcc(profile).replace(/^ال/, '')}`;
}
/** «مراجعيك» / «زبائنك» / «عملاءك» (your customers); a profile without a drawn form falls back to the default. */
export function yourCustomers(profile: TermProfile): string {
  const words: ProfileWords = { default: 'زبائنك', clinic: 'مراجعيك', gulf: 'عملاءك' };
  return words[profile] ?? words.default;
}

export const PANEL = {
  headline: ['المدينة كلّها', 'تحجز عندك'],
  previewHeadline: ['صفحتك تتكوّن', 'وأنت تكتب'],
  doneHeadline: 'صفحتك منشورة',
  previewSub: (profile: TermProfile) => `كل جواب يظهر هنا كما سيراه ${term('customers', profile)}.`.replace('سيراه المراجعون', 'سيراه مراجعوك').replace('سيراه الزبائن', 'سيراه زبائنك').replace('سيراه العملاء', 'سيراه عملاؤك'),
  doneSub: 'شاركها على واتساب أو اطبع رمز QR من «اليوم».',
  sub: {
    role: 'ابدأ من المتصفّح بلا تطبيق؛ التطبيق يأتي لاحقاً بالحساب نفسه.',
    join: 'فريقك على صفحة واحدة؛ الدعوة برمز من صاحب النشاط أو برقمك.',
    phone: 'رقمك هو حسابك، على الويب وفي التطبيق.',
    otp: 'التحقّق عبر واتساب، بلا كلمة سرّ تُنسى.',
    pin: 'ستة أرقام تدخل بها من أي جهاز.',
    error: 'ما كتبته محفوظ؛ نكمل من حيث توقّفت.',
  },
} as const;

export const SHELL = {
  enter: 'أو اضغط',
  enterKey: 'Enter ↵',
  terms: 'الشروط',
  privacy: 'الخصوصية',
  help: 'المساعدة',
  offline: 'بلا اتصال · نتابع تلقائياً عند عودة الاتصال',
  /** «بالمتابعة توافق على الشروط والخصوصية.» */
  consentBefore: 'بالمتابعة توافق على ',
  consentAnd: ' و',
  consentAfter: '.',
  retry: 'أعد المحاولة',
  continue: 'متابعة',
  more: (n: string) => `+${n}`,
} as const;

export const ERRORS = {
  title: 'لم تنجح المحاولة.',
  network: 'تحقّق من الاتصال ثم أعد المحاولة. ما حُرّر على هذا الجهاز محفوظ محلياً.',
  server: 'الخلل عندنا لا عندك، وما كتبته محفوظ على هذا الجهاز. أعد المحاولة بعد قليل، وإن تكرّر أرسل الرقم المرجعي إلى المساعدة.',
  ref: 'الرقم المرجعي',
} as const;

export const ROLE = {
  title: 'كيف ستستخدم احجزلي للأعمال؟',
  sub: 'يمكنك إضافة الخيار الآخر لاحقاً من الإعدادات.',
  owner: 'أنا صاحب النشاط',
  ownerSub: 'أنشئ نشاطك من المتصفّح وابدأ باستقبال الحجوزات، بلا تطبيق.',
  member: 'عندي رمز دعوة',
  memberSub: 'انضمّ إلى فريق نشاط موجود؛ الرمز في رسالة الواتساب من صاحب النشاط.',
  haveAccount: 'عندك حساب؟ سجّل الدخول',
  crumbOwner: 'صاحب النشاط',
  crumbMember: 'عندي رمز دعوة',
  crumbStaff: 'موظف',
} as const;

export const JOIN = {
  title: 'ما رمز الدعوة؟',
  // Board: «ستة أحرف وأرقام لاتينية من رسالة الواتساب … الرابط في الرسالة يفتح
  // هذه الشاشة والرمز معبّأ.» — the wire issues a 48-hex link token (profile
  // invites, ruling S8-L10), not a six-character code, so the first clause is
  // dropped rather than stated falsely (open question logged).
  sub: 'الرابط في رسالة الواتساب التي وصلتك من صاحب النشاط يفتح هذه الشاشة والرمز معبّأ. يمكنك لصق الرابط أو الرمز هنا.',
  placeholder: 'ehjizli.daralulum.com/join/…',
  invalid: 'الرمز غير صحيح أو انتهت صلاحيته. اطلب رمزاً جديداً من صاحب النشاط.',
  noCode: 'ما وصلني رمز؟ تابع برقمك',
  found: (business: string, role: string) => `دعوة إلى «${business}» · ${role}`,
  busy: 'جارٍ التحقّق…',
  waitingTitle: 'بانتظار دعوة',
  waitingSub: 'أعطِ رقمك لصاحب النشاط ليضيفك إلى الفريق. تظهر الدعوة هنا تلقائياً.',
  yourNumber: 'رقمك',
  copy: 'نسخ الرقم',
  copied: 'نُسخ الرقم.',
  polling: 'نتحقّق من الدعوات كل دقيقة',
  haveCode: 'لديّ رمز دعوة',
  ownBusiness: 'أملك نشاطاً؟ أنشئه',
  back: 'رجوع',
  otherPhone: (name: string, role: string) => `الدعوة إلى «${name}» (${role}) مرسلة إلى رقم آخر. اطلب من صاحب النشاط أن يدعو رقمك.`,
  acceptedTitle: (name: string) => `أصبحتَ ضمن فريق ${name}`,
  acceptedSub: (profile: TermProfile) => `سترى ${term('appointments', profile)} في «اليوم»، ويمكنك التبديل بين الأنشطة من أعلى الشاشة.`,
  acceptedLine: (role: string) => `أنت ضمن الفريق · ${role}`,
  openToday: 'افتح اليوم',
} as const;

export const PHONE = {
  title: 'ما رقم هاتفك؟',
  sub: 'نرسل رمز تحقّق عبر واتساب، ثم تختار رمزاً سرياً من ٦ أرقام.',
  invalid: 'الرقم غير مكتمل. الصيغة: ',
  mask: '07XX XXX XXXX',
  exists: 'هذا الرقم مسجّل عندنا. سجّل الدخول برمزك السري، أو استخدم رقماً آخر.',
  signIn: 'سجّل الدخول',
  otherNumber: 'رقم آخر',
  sending: 'جارٍ الإرسال…',
  closed: 'التسجيل متوقّف مؤقتاً. حاول لاحقاً أو تواصل مع الدعم.',
  peek: 'أدخل رمز التحقّق',
} as const;

export const OTP = {
  title: 'أدخل رمز التحقّق',
  // The sentence names the channel that REALLY delivered the code (ihjizli-otp's
  // readback). subBefore is the default — the WhatsApp sentence — and is what an
  // unknown channel says, exactly as the board draws it.
  subBefore: 'أرسلنا الرمز عبر واتساب إلى ',
  subBeforeTelegram: 'أرسلنا الرمز عبر تيليغرام إلى ',
  subBeforeSms: 'أرسلنا الرمز برسالة نصية إلى ',
  subAfter: '.',
  verify: 'تحقّق',
  busy: 'جارٍ التحقق…',
  resendIn: 'أعد الإرسال بعد ',
  resend: 'أعد الإرسال',
  resent: 'أُرسل رمز جديد عبر واتساب.',
  resentTelegram: 'أُرسل رمز جديد عبر تيليغرام.',
  resentSms: 'أُرسل رمز جديد برسالة نصية.',
  // The exchange's real `attempts_left` (rule 7: never a number the server did not give).
  attemptsLeft: (n: number): string =>
    n === 1 ? 'بقيت لك محاولة واحدة.' : n === 2 ? 'بقيت لك محاولتان.' : n <= 10 ? `بقيت لك ${n} محاولات.` : `بقيت لك ${n} محاولة.`,
  change: 'تغيير الرقم',
  incomplete: 'الرمز غير مكتمل.',
  locked: 'المحاولات متوقفة مؤقتاً · ',
  lockedSub: 'تعود الخلايا للعمل عندما ينتهي العدّ.',
  resendStopped: 'إعادة الإرسال متوقفة أيضاً',
  peek: 'اختر رمزك السري',
} as const;

export const PIN = {
  title: 'اختر رمزك السري',
  sub: '٦ أرقام تدخل بها كل مرة، على الويب وفي التطبيق.',
  rulesBefore: 'لا يكون متسلسلاً ',
  rulesMid: ' ولا مكرّراً ',
  rulesAfter: ' ولا من أرقام هاتفك.',
  keyboard: 'اكتب الأرقام من لوحة المفاتيح',
  confirmTitle: 'أكّد رمزك السري',
  confirmSub: 'أدخل الرمز نفسه مرة ثانية.',
  save: 'حفظ',
  busy: 'جارٍ الحفظ…',
  mismatch: 'الرمزان غير متطابقين. أعد الإدخال.',
  weak: 'هذا الرمز سهل التخمين. اختر رمزاً آخر.',
  incomplete: 'الرمز السري ستة أرقام.',
  restart: 'ابدأ من جديد',
  peekConfirm: 'أكّد رمزك السري',
  label: 'الرمز السري',
  confirmLabel: 'أكّد الرمز السري',
  crumb: 'الرمز السري',
} as const;

/**
 * «ما اسمك؟» — the owner's own name, asked right after the PIN (owner ruling
 * 2026-10-24: a clone of the business-name question, nothing else redrawn).
 * The refusals are profile's own sentences (422 full_name required / too_long).
 */
export const OWNER_NAME = {
  title: 'ما اسمك؟',
  sub: 'يظهر لفريقك وفي سجلّ المواعيد.',
  placeholder: 'مثال: علي حسن',
  required: 'الاسم مطلوب',
  tooLong: 'الاسم طويل جداً',
} as const;

export const BUSINESS = {
  nameTitle: 'ما اسم نشاطك؟',
  nameSub: (profile: TermProfile) => `كما تريده أن يظهر ${forCustomers(profile)} على صفحتك وفي الرسائل.`,
  namePlaceholder: 'مثل: عيادة د. سارة الحسيني',
  nameRequired: 'اسم النشاط مطلوب.',
  typeTitle: 'ما نوع نشاطك؟',
  typeSub: 'النوع يحدّد شكل الحجز وخطوات الإعداد.',
  typeRequired: 'اختر نوع النشاط.',
  allTypes: 'كل الأنواع',
  searchType: 'ابحث عن نوع',
  noMatch: 'لا نوع بهذا الاسم.',
  shapeBefore: 'الحجز في هذه الفئة يكون على ',
  shapeLater: 'غيّره لاحقاً من الإعدادات',
  cityTitle: 'في أي مدينة؟',
  citySub: 'المدينة تحدّد ما يراه القريبون منك؛ الموقع الدقيق على الخريطة لاحقاً من «أكمل ملفك».',
  otherCity: 'مدينة أخرى',
  cityPlaceholder: 'اكتب اسم المدينة',
  cityRequired: 'المدينة مطلوبة',
  addressTitle: 'ما عنوان النشاط؟',
  addressSub: 'اختياري الآن. يظهر تحت الاسم في صفحتك وفي رسائل التذكير.',
  addressPlaceholder: 'المنصور · شارع ١٤ رمضان',
  skip: 'تخطّي',
  phoneTitle: 'ما هاتف النشاط؟',
  phoneSub: (profile: TermProfile) => `الرقم الذي يراه ${term('customers', profile)} للاتصال؛ افتراضياً رقمك.`,
  peekType: 'ما نوع نشاطك؟',
  peekCity: 'في أي مدينة؟',
  peekAddress: 'ما عنوان النشاط؟',
  peekPhone: 'ما هاتف النشاط؟',
} as const;

/**
 * «عندك أكثر من فرع؟» (board 26 business/enroll/branches--v2), asked on the web
 * after «في أي مدينة؟» only when the platform has branches on (ruling R11/R12).
 */
export const BRANCHES_Q = {
  title: 'عندك أكثر من فرع؟',
  sub: 'فروع تحت نشاط واحد: زبائن وتقارير مشتركة، ولكل فرع فريقه وساعاته وصندوقه.',
  multi: 'نعم، أكثر من فرع',
  multiSub: 'نضيف الفرع الأول الآن والبقية من الإعدادات',
  firstName: 'اسم الفرع الأول',
  /** Adapted — ruling W8: the drawn «نشاطي › الفروع» is the mobile path. */
  note: 'الفرع الثاني وما بعده من «الإعدادات › الفروع والصناديق»؛ لكل فرع مدير وفريق وصندوق.',
  /** Undrawn — ruling W11. */
  nameRequired: 'اسم الفرع مطلوب.',
} as const;

export const HOURS = {
  title: (profile: TermProfile) => `متى تستقبل ${customersAcc(profile)}؟`,
  sub: 'الأسبوع يبدأ السبت والجمعة عطلة حسب العراق. الأوقات افتراض الفئة؛ عدّل يوماً ثم انسخه.',
  copy: 'نسخ إلى بقية الأيام',
  copyMobile: 'نسخ وقت السبت إلى بقية الأيام',
  closed: 'مغلق',
  weekendClosed: 'عطلة · مغلق',
  noDay: 'افتح يوماً واحداً على الأقل.',
  order: 'وقت الإغلاق بعد وقت الفتح.',
  saving: 'جارٍ الحفظ…',
  crumb: 'ساعات الدوام',
  from: 'من',
  to: 'إلى',
  daily: 'يومياً',
  /** Shape 6 (table) only — undrawn, ruling W2. */
  sitting: 'مدة الجلسة',
  sittingOptions: [
    { min: 60, label: '٦٠ دقيقة' },
    { min: 90, label: '٩٠ دقيقة' },
    { min: 120, label: '١٢٠ دقيقة' },
  ],
} as const;

/** Shape 3's step 5 (undrawn — ruling W1): the work days, toggles only. */
export const WORKDAYS = {
  title: 'ما أيام العمل؟',
  sub: 'لا أوقات دقيقة؛ الزبون يختار اليوم والفترة.',
} as const;

/** Shape 4's step 5 (board 22 enroll/handover--shape-4, verbatim; «أيام مغلقة بشكل دائم» omitted — R9/G6). */
export const HANDOVER = {
  title: (profile: TermProfile) => `متى يستلم ${term('customer', profile)} ويسلّم؟`,
  sub: 'تنطبق على كل الأنواع، ويمكن تخصيصها لكل نوع لاحقاً.',
  checkIn: 'وقت الدخول',
  checkOut: 'وقت الخروج',
  note: 'لتأجير السيارات والمعدّات: الاستلام والتسليم في اليوم نفسه، والحساب باليوم.',
  /** The preview chip «الدخول ٣:٠٠ م · الخروج ١٢:٠٠ م». */
  chip: (checkIn: string, checkOut: string) => `الدخول ${checkIn} · الخروج ${checkOut}`,
  invalid: 'الوقت غير صالح.',
} as const;

export const SHAPE1 = {
  stepLabel: 'الفريق',
  title: 'من يعمل معك؟',
  sub: 'أدخل رقم الزميل؛ تصله الدعوة في تطبيق الأعمال إن كان مثبّتاً، وإلا برسالة واتساب فيها رمز ورابط إلى هذه المنصّة.',
  placeholder: '770 000 0000',
  add: 'دعوة',
  you: 'أنت · المالك',
  sent: 'دعوة مرسلة',
  joined: 'انضمّت',
  joinedM: 'انضمّ',
  note: (customersAccusative: string, staffDef: string, staffDual: string) =>
    `يمكنك تخطّي هذه الخطوة والعمل وحدك الآن. خيار «اختيار ${staffDef}» يظهر لل${customersAccusative.replace(/^ال/, '')} فقط عند ${staffDual} فأكثر وبسماحك.`,
  finish: 'إنهاء الإعداد',
  skipNow: 'تخطّي الآن',
  teamLine: (names: string) => `الفريق: ${names}`,
} as const;

/** Every shape step 2–6 (board 25 v2 shape--n). */
export const SHAPE_COMMON = {
  finish: 'إنهاء الإعداد',
  /** Finish disabled until one record (R8) — undrawn, ruling W12. */
  atLeastOne: 'أضف واحداً على الأقل.',
  /** Row field names (board 22 entity captions). */
  name: 'الاسم',
  price: 'السعر',
} as const;

/** Shape 2 (board 25 v2 shape--2, board 22 §ج): places + the hourly price bands. */
export const PLACES = {
  active: 'نشط',
  paused: 'متوقّف',
  /** «إضافة ملعب» — the indefinite place noun from the terminology. */
  add: (placeSing: string) => `إضافة ${placeSing}`,
  bandsTitle: 'سعر الساعة حسب الوقت',
  addBand: 'إضافة فترة سعرية',
  /** The preview line's tail «… · السعر بالساعة». */
  perHour: 'السعر بالساعة',
} as const;

/** Shape 3 (board 25 v2 shape--3): periods + the one deposit control (R18). */
export const PERIODS = {
  custom: 'فترة مخصّصة',
  deposit: 'العربون',
  percent: 'نسبة من المبلغ',
  fixed: 'مبلغ ثابت',
  onConfirm: 'عند التأكيد',
  /** The hours chip «{days} · فترتان / ٣ فترات». */
  periodsWords: { one: 'فترة واحدة', two: 'فترتان', few: 'فترات', many: 'فترة' },
} as const;

/** Shape 4 (board 25 v2 shape--4, board 24 unit-type editor). */
export const UNIT = {
  add: 'إضافة نوع وحدة',
  addSub: 'أضف نوعاً واحداً الآن على الأقل. أسعار نهاية الأسبوع والمواسم والاستخدام النهاري من داخل النوع.',
  panelTitle: 'نوع الوحدة',
  name: 'الاسم',
  /** Undrawn — ruling H5 (the unit-type side panel's short description). */
  description: 'وصف قصير',
  descriptionHint: 'يظهر للزبون تحت اسم النوع.',
  quantity: 'العدد',
  minNights: 'الحدّ الأدنى للمدة',
  photos: 'الصور',
  priceUnit: 'وحدة التسعير',
  perNight: 'لليلة',
  perDay: 'لليوم',
  priceNight: 'السعر لليلة',
  /** Undrawn — ruling W4. */
  priceDay: 'السعر لليوم',
  weekend: 'سعر نهاية الأسبوع',
  seasons: 'المواسم',
  seasonAdd: 'إضافة',
  dayUse: 'استخدام نهاري',
  dayUseSub: 'فترة في اليوم نفسه بسعر خاص',
  deposit: 'العربون',
  depositPercent: 'نسبة من المبلغ',
  depositFixed: 'مبلغ ثابت لليلة',
  /** Undrawn — ruling W5 (the handover labels). */
  checkIn: 'وقت الدخول',
  checkOut: 'وقت الخروج',
  save: 'حفظ',
  atLeast: 'على الأقل',
  /** «/ ليلة» | «/ يوم» after the price. */
  per: (unit: string) => (unit === 'day' ? 'يوم' : 'ليلة'),
  /** Count words (Arabic agreement through terminology countLabel). */
  units: { one: 'وحدة واحدة', two: 'وحدتان', few: 'وحدات', many: 'وحدة' },
  nights: { one: 'ليلة واحدة', two: 'ليلتان', few: 'ليالٍ', many: 'ليلة' },
  days: { one: 'يوم واحد', two: 'يومان', few: 'أيام', many: 'يوماً' },
} as const;

/** Shape 5 (board 25 v2 shape--5; panel field names undrawn — ruling W6, from board 22's entity captions). */
export const PROGRAMS = {
  add: 'إضافة برنامج',
  weekly: 'يتكرّر أسبوعياً',
  once: 'جلسة واحدة',
  wholeCourse: 'تُحجز كاملة',
  name: 'اسم البرنامج',
  trainer: 'المدرّب',
  seatPrice: 'سعر المقعد',
  kind: 'نوع الحجز',
  kindSession: 'جلسة',
  kindCourse: 'الدورة كاملة',
  meetingPoint: 'نقطة التجمّع',
  first: 'أول جلسة',
  date: 'التاريخ',
  time: 'الوقت',
  duration: 'المدة بالدقائق',
  seats: 'المقاعد',
  sessionsCount: 'عدد الجلسات',
  save: 'حفظ',
  /** «/ مقعد» | «/ الدورة» after the price. */
  perSeat: 'مقعد',
  perCourse: 'الدورة',
  seatsCount: (n: string) => `${n} مقعداً`,
  sessionsWords: { one: 'جلسة واحدة', two: 'جلستان', few: 'جلسات', many: 'جلسة' },
  programsWords: { one: 'برنامج واحد', two: 'برنامجان', few: 'برامج', many: 'برنامجاً' },
  weeklySeries: 'بجلسات أسبوعية',
} as const;

/** Shape 6 (board 25 v2 shape--6, board 24; the deposit's on state undrawn — ruling W7). */
export const TABLES = {
  zones: { indoor: 'داخلي', outdoor: 'خارجي', family: 'عائلي' },
  seats: (n: string) => `${n} مقاعد`,
  add: 'إضافة طاولة',
  defaultName: (n: string) => `طاولة ${n}`,
  name: 'الاسم',
  seatsLabel: 'المقاعد',
  zone: 'المنطقة',
  deposit: 'العربون',
  depositOff: 'مطفأ · الحجز بلا دفع مسبق',
  perPerson: 'للشخص',
  perTable: 'للطاولة',
  amount: 'المبلغ',
  tablesWords: { one: 'طاولة واحدة', two: 'طاولتان', few: 'طاولات', many: 'طاولة' },
  zonesWords: { one: 'منطقة واحدة', two: 'منطقتين', few: 'مناطق', many: 'منطقة' },
  sitting: (min: string) => `الجلسة ${min} دقيقة`,
} as const;

export const DONE = {
  title: 'نشاطك جاهز',
  sub: (profile: TermProfile) => `صفحتك منشورة. افتح «اليوم» لتضيف الخدمات والأسعار وتشارك رابطك، أو استورد ${yourCustomers(profile)} ومواعيدك من المربع أولاً.`,
  subMobile: (profile: TermProfile) => `صفحتك منشورة. افتح «اليوم» لتضيف الخدمات والأسعار، أو استورد ${yourCustomers(profile)} من المربع.`,
  openToday: 'افتح اليوم',
  import: 'استيراد من المربع',
  published: 'منشورة',
  ctaClinic: 'احجز زيارة',
  cta: 'احجز',
} as const;

export const STEP_LABELS = {
  role: 'الدور',
  join: 'رمز الدعوة',
  phone: 'الهاتف',
  pin: 'رمز الدخول',
  business: 'النشاط',
  hours: 'ساعات الدوام',
  /** Shape 3's step 5 (undrawn — ruling W1). */
  workdays: 'أيام العمل',
  /** Shape 4's step 5 (board 22 enroll/handover--shape-4). */
  handover: 'الاستلام والتسليم',
} as const;

/**
 * The six shapes' words (board 22 §أ / 25 §14.3 «الحجز في هذه الفئة يكون على
 * {word}»), their sixth-step label / question / sub (board 25 v2 shape--n).
 * Ruling R7: the type-step sentence of shapes 2–6 is the shape step's drawn
 * sub; only the staff sentence is drawn on the type step (staffShapeSentence).
 * The place nouns come from the terminology (ruling R6), never inline.
 */
export const SHAPE_COPY = {
  staff: {
    word: 'موظف',
    label: () => SHAPE1.stepLabel,
    question: () => SHAPE1.title,
    sub: () => SHAPE1.sub,
  },
  place: {
    word: 'مكان أو جهاز',
    label: (categoryId: string) => placeTerms(categoryId).placePluralDef,
    question: (categoryId: string) => `كم ${placeTerms(categoryId).placeAcc} لديك؟`,
    /** Drawn «الزبون لا يختار الملعب؛ النظام يعطيه أول ملعب فارغ.» */
    sub: (categoryId: string, profile: TermProfile = 'default') => {
      const p = placeTerms(categoryId);
      return `${term('customer', profile)} لا يختار ${p.placeSingDef}؛ النظام يعطيه أول ${p.placeSing} فارغ.`;
    },
  },
  business: {
    word: 'النشاط كله',
    label: () => 'الفترات',
    question: () => 'ما فترات الحجز؟',
    sub: () => 'لا أوقات دقيقة؛ الزبون يختار اليوم والفترة، والعربون يؤكّد الحجز.',
  },
  unit: {
    word: 'وحدة',
    label: () => 'أنواع الوحدات',
    question: () => 'ماذا يحجز الزبون؟',
    sub: () => 'الشاليه نوع واحد عدده ١. الفندق أنواع غرف، وتأجير السيارات أنواع سيارات.',
  },
  seat: {
    word: 'مقعد',
    label: () => 'البرامج',
    question: () => 'ما برامجك؟',
    sub: () => 'كل برنامج له جلسات بموعد وعدد مقاعد. المدرّب للمعلومة فقط؛ الزبون لا يختاره.',
  },
  table: {
    word: 'طاولة',
    label: () => 'الطاولات',
    question: () => 'طاولاتك ومناطقها',
    sub: () => 'النظام يختار الطاولة للزبون بحسب عدد الضيوف. مدة الجلسة الافتراضية ٩٠ دقيقة من ساعات الدوام.',
  },
} as const;
